/**
 * Wohnansicht Configurator
 * Room view image viewer with filtering by frame type and painting dimensions.
 * Custom element: <wohnansicht-configurator>
 */
if (!customElements.get('wohnansicht-configurator')) {
  class WohnansichtConfigurator extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.visibleIndices = [];
      this.touchStartX = 0;
      this.touchDeltaX = 0;
    }

    connectedCallback() {
      // Read range bounds from data attributes (set by Liquid)
      this.rangeMin = parseInt(this.dataset.rangeMin) || 10;
      this.rangeMax = parseInt(this.dataset.rangeMax) || 350;
      this.rangeStep = parseInt(this.dataset.rangeStep) || 10;
      this.noLimitThreshold = this.rangeMax - this.rangeStep;

      this.filterState = {
        frame: 'all',
        widthMin: this.rangeMin,
        widthMax: this.rangeMax,
        heightMin: this.rangeMin,
        heightMax: this.rangeMax,
      };

      this.slides = [...this.querySelectorAll('.wohnansicht__slide')];
      this.thumbs = [...this.querySelectorAll('.wohnansicht__thumb')];
      this.mainImage = this.querySelector('.wohnansicht__main-image');
      this.noResults = this.querySelector('.wohnansicht__no-results');
      this.resetBtn = this.querySelector('.wohnansicht__reset-btn');

      if (this.slides.length === 0) return;

      this.initFilters();
      this.initNavigation();
      this.initThumbnails();
      this.initSwipe();
      this.filterImages();
    }

    /* ====================================================================
       Filters
       ==================================================================== */

    initFilters() {
      // Frame toggle buttons
      const frameBtns = this.querySelectorAll('.wohnansicht__btn[data-frame]');
      frameBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          frameBtns.forEach((b) => {
            b.classList.remove('wohnansicht__btn--active');
            b.setAttribute('aria-checked', 'false');
          });
          btn.classList.add('wohnansicht__btn--active');
          btn.setAttribute('aria-checked', 'true');
          this.filterState.frame = btn.dataset.frame;
          this.filterImages();
          this.showResetIfNeeded();
        });
      });

      // Dual-range sliders
      this.querySelectorAll('.wohnansicht__range-wrap').forEach((wrap) => {
        const dim = wrap.dataset.dimension;
        const minInput = wrap.querySelector('[data-bound="min"]');
        const maxInput = wrap.querySelector('[data-bound="max"]');

        const onInput = () => {
          let minVal = parseInt(minInput.value);
          let maxVal = parseInt(maxInput.value);

          // Prevent crossover
          if (minVal > maxVal) {
            minInput.value = maxVal;
            minVal = maxVal;
          }

          this.filterState[dim + 'Min'] = minVal;
          this.filterState[dim + 'Max'] = maxVal;
          this.updateRangeVisual(wrap);
          this.filterImages();
          this.showResetIfNeeded();
        };

        minInput.addEventListener('input', onInput);
        maxInput.addEventListener('input', onInput);

        // Initialize visual state
        this.updateRangeVisual(wrap);
      });

      // Reset button
      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', () => this.resetFilters());
      }
    }

    updateRangeVisual(wrap) {
      const minInput = wrap.querySelector('[data-bound="min"]');
      const maxInput = wrap.querySelector('[data-bound="max"]');
      const fill = wrap.querySelector('.wohnansicht__range-fill');
      const labels = wrap.closest('.wohnansicht__filter-group').querySelector('.wohnansicht__range-labels');

      const rangeMin = parseInt(minInput.min);
      const rangeMax = parseInt(minInput.max);
      const minVal = parseInt(minInput.value);
      const maxVal = parseInt(maxInput.value);
      const span = rangeMax - rangeMin;

      const leftPct = ((minVal - rangeMin) / span) * 100;
      const rightPct = 100 - ((maxVal - rangeMin) / span) * 100;

      fill.style.left = leftPct + '%';
      fill.style.right = rightPct + '%';

      if (labels) {
        const minLabel = labels.querySelector('[data-label="min"]');
        const maxLabel = labels.querySelector('[data-label="max"]');
        minLabel.textContent = this.formatSize(minVal);
        maxLabel.textContent = this.formatSize(maxVal);
      }
    }

    formatSize(val) {
      return val >= this.noLimitThreshold ? this.noLimitThreshold + '+ cm' : val + ' cm';
    }

    /* ====================================================================
       Filtering Logic
       ==================================================================== */

    filterImages() {
      const { frame, widthMin, widthMax, heightMin, heightMax } = this.filterState;
      this.visibleIndices = [];

      this.slides.forEach((slide, i) => {
        const sFrame = slide.dataset.frame;
        const sWidth = parseInt(slide.dataset.width);
        const sHeight = parseInt(slide.dataset.height);

        let visible = true;

        // Frame filter
        if (frame !== 'all') {
          visible = visible && sFrame === frame;
        }

        // Width filter (at threshold = no upper limit)
        const effWidthMax = widthMax >= this.noLimitThreshold ? Infinity : widthMax;
        visible = visible && sWidth >= widthMin && sWidth <= effWidthMax;

        // Height filter
        const effHeightMax = heightMax >= this.noLimitThreshold ? Infinity : heightMax;
        visible = visible && sHeight >= heightMin && sHeight <= effHeightMax;

        if (visible) {
          slide.removeAttribute('data-filtered');
          if (this.thumbs[i]) this.thumbs[i].removeAttribute('data-filtered');
          this.visibleIndices.push(i);
        } else {
          slide.setAttribute('data-filtered', 'out');
          if (this.thumbs[i]) this.thumbs[i].setAttribute('data-filtered', 'out');
        }
      });

      if (this.visibleIndices.length > 0) {
        const wasEmpty = this.noResults.style.display === 'flex';
        this.noResults.style.display = 'none';
        if (wasEmpty || !this.visibleIndices.includes(this.currentIndex)) {
          this.selectSlide(this.visibleIndices[0]);
        }
      } else {
        this.noResults.style.display = 'flex';
        this.slides.forEach((s) => s.classList.remove('wohnansicht__slide--active'));
        this.thumbs.forEach((t) => t.classList.remove('wohnansicht__thumb--active'));
      }

      this.updateNavVisibility();
    }

    /* ====================================================================
       Slide Selection & Navigation
       ==================================================================== */

    selectSlide(index) {
      if (index < 0 || index >= this.slides.length) return;

      this.slides[this.currentIndex]?.classList.remove('wohnansicht__slide--active');
      this.thumbs[this.currentIndex]?.classList.remove('wohnansicht__thumb--active');

      this.currentIndex = index;

      this.slides[index]?.classList.add('wohnansicht__slide--active');
      this.thumbs[index]?.classList.add('wohnansicht__thumb--active');

      // Scroll active thumbnail into view
      this.thumbs[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }

    nextSlide() {
      if (this.visibleIndices.length <= 1) return;
      const pos = this.visibleIndices.indexOf(this.currentIndex);
      const next = (pos + 1) % this.visibleIndices.length;
      this.selectSlide(this.visibleIndices[next]);
    }

    prevSlide() {
      if (this.visibleIndices.length <= 1) return;
      const pos = this.visibleIndices.indexOf(this.currentIndex);
      const prev = (pos - 1 + this.visibleIndices.length) % this.visibleIndices.length;
      this.selectSlide(this.visibleIndices[prev]);
    }

    initNavigation() {
      this.querySelector('.wohnansicht__nav--prev')?.addEventListener('click', () => this.prevSlide());
      this.querySelector('.wohnansicht__nav--next')?.addEventListener('click', () => this.nextSlide());
    }

    updateNavVisibility() {
      const show = this.visibleIndices.length > 1;
      const prev = this.querySelector('.wohnansicht__nav--prev');
      const next = this.querySelector('.wohnansicht__nav--next');
      if (prev) prev.style.display = show ? '' : 'none';
      if (next) next.style.display = show ? '' : 'none';
    }

    /* ====================================================================
       Thumbnails
       ==================================================================== */

    initThumbnails() {
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this.selectSlide(parseInt(thumb.dataset.index));
        });
      });

      const track = this.querySelector('.wohnansicht__thumb-track');
      this.querySelector('.wohnansicht__thumb-arrow--prev')?.addEventListener('click', () => {
        track.scrollBy({ left: -200, behavior: 'smooth' });
      });
      this.querySelector('.wohnansicht__thumb-arrow--next')?.addEventListener('click', () => {
        track.scrollBy({ left: 200, behavior: 'smooth' });
      });
    }

    /* ====================================================================
       Touch Swipe (mobile main image)
       ==================================================================== */

    initSwipe() {
      if (!this.mainImage) return;

      this.mainImage.addEventListener(
        'touchstart',
        (e) => {
          this.touchStartX = e.touches[0].clientX;
          this.touchDeltaX = 0;
        },
        { passive: true }
      );

      this.mainImage.addEventListener(
        'touchmove',
        (e) => {
          this.touchDeltaX = e.touches[0].clientX - this.touchStartX;
        },
        { passive: true }
      );

      this.mainImage.addEventListener('touchend', () => {
        if (Math.abs(this.touchDeltaX) > 50) {
          if (this.touchDeltaX < 0) {
            this.nextSlide();
          } else {
            this.prevSlide();
          }
        }
        this.touchDeltaX = 0;
      });
    }

    /* ====================================================================
       Reset
       ==================================================================== */

    resetFilters() {
      this.filterState = {
        frame: 'all',
        widthMin: this.rangeMin,
        widthMax: this.rangeMax,
        heightMin: this.rangeMin,
        heightMax: this.rangeMax,
      };

      // Reset frame buttons
      this.querySelectorAll('.wohnansicht__btn[data-frame]').forEach((btn) => {
        const isAll = btn.dataset.frame === 'all';
        btn.classList.toggle('wohnansicht__btn--active', isAll);
        btn.setAttribute('aria-checked', isAll ? 'true' : 'false');
      });

      // Reset range sliders
      this.querySelectorAll('.wohnansicht__range-wrap').forEach((wrap) => {
        wrap.querySelector('[data-bound="min"]').value = this.rangeMin;
        wrap.querySelector('[data-bound="max"]').value = this.rangeMax;
        this.updateRangeVisual(wrap);
      });

      this.filterImages();
      this.showResetIfNeeded();
    }

    showResetIfNeeded() {
      if (!this.resetBtn) return;
      const { frame, widthMin, widthMax, heightMin, heightMax } = this.filterState;
      const isDefault =
        frame === 'all' &&
        widthMin <= this.rangeMin &&
        widthMax >= this.rangeMax &&
        heightMin <= this.rangeMin &&
        heightMax >= this.rangeMax;
      this.resetBtn.style.display = isDefault ? 'none' : '';
    }
  }

  customElements.define('wohnansicht-configurator', WohnansichtConfigurator);
}
